import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { format } from 'date-fns';
import { cn } from '@/utils/utils.ts';
import { DeviceMetricsTableCard } from '@/features/devices/components/device-metrics-table/device-metrics-table-card.tsx';
import { useGetDeviceQuery } from '@/features/devices/hooks/queries/[device]/get-device.ts';
import { useOverviewDeviceQuery } from '@/features/devices/hooks/queries/[device]/get-device-overview.ts';
import { getFieldName } from '@/features/devices/schemas/devices.metadata.ts';

export function DefaultDevicePage({ deviceId }: { deviceId: string | undefined }) {
  const { data: device, isLoading: isDeviceLoading } = useGetDeviceQuery({
    deviceId: deviceId ?? '',
  });

  const { data: deviceOverview, isLoading: isDeviceOverviewLoading } = useOverviewDeviceQuery({
    deviceId: deviceId ?? '',
  });

  const deviceStatuses = deviceOverview?.device.filter((d) => {
    return device?.type
      ? String(getFieldName(device?.type, d?.field)).startsWith('Status') ||
          String(getFieldName(device?.type, d?.field)).endsWith('Status')
      : false;
  });

  const isLoading = isDeviceLoading || isDeviceOverviewLoading;

  return (
    <>
      <DashboardCard isLoading={isLoading} className="min-h-48">
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-semibold text-nowrap">Device details</h3>
          <div>
            <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @xl:grid-cols-4">
              <div className="flex flex-col gap-1 px-2">
                <span className="text-muted-foreground text-sm text-nowrap">
                  Serial number
                </span>
                <div className="flex w-full items-end justify-between gap-2">
                  <div>
                    <span className="font-semibold">{device?.SN}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 px-2">
                <span className="text-muted-foreground text-sm text-nowrap">Supplier</span>
                <div className="flex w-full items-end justify-between gap-2">
                  <div>
                    <span className="font-semibold">{device?.supplier}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 px-2">
                <span className="text-muted-foreground text-sm text-nowrap">Plant name</span>
                <div className="flex w-full items-end justify-between gap-2">
                  <div>
                    <span className="font-semibold">{device?.plantName}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 px-2">
                <span className="text-muted-foreground text-sm text-nowrap">Location</span>
                <div className="flex w-full items-end justify-between gap-2">
                  <div>
                    <span className="font-semibold">
                      {device?.plantCountry}
                      {', '}
                      {device?.plantCity}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 px-2">
                <span className="text-muted-foreground text-sm text-nowrap">Connected at</span>
                <div className="flex w-full items-end justify-between gap-2">
                  <div>
                    <span className="font-semibold">
                      {device?.connectedAt
                        ? format(new Date(device.connectedAt), 'dd-MM-yyyy HH:mm')
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 px-2">
                <span className="text-muted-foreground text-sm text-nowrap">Show device</span>
                <div className="flex w-full items-end justify-between gap-2">
                  <div>
                    <span className="font-semibold">
                      {device?.showInDashboard ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardCard>

      <DashboardCard isLoading={isLoading} className="min-h-28">
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-semibold text-nowrap">State</h3>
          <div>
            <div
              className={cn(
                'grid',
                deviceStatuses?.length
                  ? 'grid-cols-1 gap-4 @sm:grid-cols-2 @xl:grid-cols-4'
                  : 'grid-cols-1'
              )}
            >
              {deviceStatuses?.length
                ? deviceStatuses?.map((dS) => {
                    return (
                      <div className="flex flex-col gap-1 px-2" key={dS?.field}>
                        <span className="text-muted-foreground text-sm text-nowrap">
                          {device?.type ? getFieldName(device?.type, dS?.field) : ''}
                        </span>
                        <div className="flex w-full items-end justify-between gap-2">
                          <div>
                            <span className="font-semibold">{dS.value}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                : 'No available state was found for this device.'}
            </div>
          </div>
        </div>
      </DashboardCard>

      <DeviceMetricsTableCard
        isLoading={isLoading}
        plantId={device?.plantId}
        data={deviceOverview?.device}
        deviceType={device?.type}
        deviceSN={device?.SN}
      />
    </>
  );
}
